import type {
  JsonValue,
  RepeatControlResult,
  RepeatIterationRecord,
  RepeatProgress,
} from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';
import {
  latestRepeatProgress,
  parseRepeatControlResult,
} from './repeat-control';
import { getSkipExplanation, skippedSentence } from './decision-trace';

/**
 * Read a finished (or running) loop back into plain words: how many
 * attempts ran, what the judge said each time, what was fed back, and why
 * it stopped. Everything here comes from the Repeat Until's own result
 * (`iterations[]`, with `judge_value`, `feedback` and `region_results`) and
 * the live `repeat_control` progress events; no logs are needed.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export interface JudgeSummary {
  /** Short verdict for a chip: "valid", "not valid", "decision: approve", "score 0.8". */
  verdict: string;
  /** true = satisfied-looking, false = not, null = unknown. */
  positive: boolean | null;
  /** Up to three plain "field: value" details (errors, reasons). */
  details: string[];
}

const short = (value: unknown, max = 60): string => {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text === undefined) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export const summariseJudge = (judgeValue: unknown): JudgeSummary => {
  const value = isRecord(judgeValue) ? judgeValue : null;
  if (!value) {
    return {
      verdict: judgeValue === undefined ? 'no verdict' : short(judgeValue),
      positive: null,
      details: [],
    };
  }
  const details: string[] = [];
  if (typeof value.is_valid === 'boolean') {
    const errors = Array.isArray(value.errors) ? value.errors : [];
    for (const error of errors.slice(0, 3)) {
      if (isRecord(error) && typeof error.message === 'string') {
        details.push(error.message);
      }
    }
    return {
      verdict: value.is_valid ? 'valid' : 'not valid',
      positive: value.is_valid,
      details,
    };
  }
  if (typeof value.decision === 'string') {
    const result = isRecord(value.result) ? value.result : null;
    const confidence = result?.confidence ?? value.confidence;
    if (typeof confidence === 'number') {
      details.push(`confidence ${Math.round(confidence * 100) / 100}`);
    }
    const reason = result?.rationale ?? result?.reason ?? value.rationale;
    if (typeof reason === 'string') details.push(short(reason, 120));
    return { verdict: `decision: ${value.decision}`, positive: null, details };
  }
  if (typeof value.score === 'number') {
    return { verdict: `score ${value.score}`, positive: null, details };
  }
  if (typeof value.done === 'boolean') {
    return {
      verdict: value.done ? 'done' : 'not done',
      positive: value.done,
      details,
    };
  }
  return { verdict: short(value), positive: null, details };
};

export interface TimelineEntry {
  iteration: number;
  status: 'completed' | 'failed' | 'running' | 'pending';
  satisfied: boolean | null;
  judge: JudgeSummary;
  judgeValue: JsonValue | undefined;
  /** The values threaded into this attempt from the previous judge, by slot. */
  feedback: Record<string, JsonValue> | null;
  failedIn: string | null;
  errorCode: string | null;
  /** Per-region-node outputs for this iteration, by node id. */
  regionOutputs: Record<string, JsonValue | undefined>;
}

const regionOutputs = (
  record: RepeatIterationRecord,
): Record<string, JsonValue | undefined> => {
  const raw = (record as unknown as { region_results?: unknown })
    .region_results;
  if (!isRecord(raw)) return {};
  const out: Record<string, JsonValue | undefined> = {};
  for (const [nodeId, entry] of Object.entries(raw)) {
    out[nodeId] = isRecord(entry)
      ? (entry.output as JsonValue | undefined)
      : undefined;
  }
  return out;
};

export const timelineFromResult = (
  result: RepeatControlResult,
  nodeNames: Record<string, string> = {},
  evaluatorId: string | null = null,
  entryId: string | null = null,
): TimelineEntry[] =>
  [...result.iterations]
    .sort((a, b) => a.iteration - b.iteration)
    .map((record) => {
      const failedNode =
        record.failed_slot === 'judge'
          ? evaluatorId
          : record.failed_slot === 'attempt'
            ? entryId
            : null;
      return {
        iteration: record.iteration,
        status: record.status,
        satisfied: record.satisfied ?? null,
        judge: summariseJudge(record.judge_value),
        judgeValue: record.judge_value,
        feedback: record.feedback ?? null,
        failedIn: failedNode ? (nodeNames[failedNode] ?? failedNode) : null,
        errorCode: record.error?.code ?? null,
        regionOutputs: regionOutputs(record),
      };
    });

/** A live timeline while running: completed iterations plus the one in flight. */
export const timelineFromProgress = (
  progress: RepeatProgress | null,
  completed: TimelineEntry[],
): TimelineEntry[] => {
  if (!progress) return completed;
  const done = new Set(completed.map((entry) => entry.iteration));
  const entries = [...completed];
  const next = progress.iteration + (progress.exit_reason ? 0 : 1);
  if (
    !progress.exit_reason &&
    next <= progress.max_iterations &&
    !done.has(next)
  ) {
    entries.push({
      iteration: next,
      status: 'running',
      satisfied: null,
      judge: { verdict: 'running', positive: null, details: [] },
      judgeValue: undefined,
      feedback: null,
      failedIn: null,
      errorCode: null,
      regionOutputs: {},
    });
  }
  return entries;
};

export interface LoopOutcome {
  /** "stopped: judge satisfied on attempt 2" and friends. */
  sentence: string;
  tone: 'success' | 'warning' | 'error' | 'running' | 'idle';
  iteration: number;
  maxIterations: number;
}

export const describeOutcome = (
  result: RepeatControlResult | null,
  progress: RepeatProgress | null,
  maxIterations: number,
  timeline: TimelineEntry[],
): LoopOutcome => {
  const cap = progress?.max_iterations ?? maxIterations;
  if (result) {
    const failed = timeline.find((entry) => entry.status === 'failed');
    if (failed) {
      return {
        sentence: `failed on attempt ${failed.iteration}${failed.failedIn ? ` in ${failed.failedIn}` : ''}${
          failed.errorCode ? `: ${failed.errorCode.replace(/_/g, ' ')}` : ''
        }`,
        tone: 'error',
        iteration: failed.iteration,
        maxIterations: cap,
      };
    }
    if (result.satisfied) {
      return {
        sentence: `stopped: judge satisfied on attempt ${result.iteration_count}`,
        tone: 'success',
        iteration: result.iteration_count,
        maxIterations: cap,
      };
    }
    return {
      sentence: `stopped: reached the ${cap}-attempt limit, not satisfied`,
      tone: 'warning',
      iteration: result.iteration_count,
      maxIterations: cap,
    };
  }
  if (progress) {
    if (progress.exit_reason === 'body_failed') {
      return {
        sentence: `failed on attempt ${progress.iteration}`,
        tone: 'error',
        iteration: progress.iteration,
        maxIterations: cap,
      };
    }
    if (progress.exit_reason === 'satisfied') {
      return {
        sentence: `stopped: judge satisfied on attempt ${progress.iteration}`,
        tone: 'success',
        iteration: progress.iteration,
        maxIterations: cap,
      };
    }
    if (progress.exit_reason === 'max_iterations_exhausted') {
      return {
        sentence: `stopped: reached the ${cap}-attempt limit, not satisfied`,
        tone: 'warning',
        iteration: progress.iteration,
        maxIterations: cap,
      };
    }
    return {
      sentence: `attempt ${Math.min(progress.iteration + 1, cap)} of ${cap} running`,
      tone: 'running',
      iteration: progress.iteration,
      maxIterations: cap,
    };
  }
  return {
    sentence: 'not run yet',
    tone: 'idle',
    iteration: 0,
    maxIterations: cap,
  };
};

/** Everything the panel and the canvas need about one loop's run. */
export const readLoopRun = (params: {
  result: unknown;
  events: NodeStatusEvent[] | undefined;
  maxIterations: number;
  nodeNames?: Record<string, string>;
  evaluatorId?: string | null;
  entryId?: string | null;
}) => {
  const result = parseRepeatControlResult(params.result);
  const progress = latestRepeatProgress(params.events);
  const completed = result
    ? timelineFromResult(
        result,
        params.nodeNames,
        params.evaluatorId ?? null,
        params.entryId ?? null,
      )
    : [];
  const timeline = result
    ? completed
    : timelineFromProgress(progress, completed);
  const outcome = describeOutcome(
    result,
    progress,
    params.maxIterations,
    timeline,
  );
  return { result, progress, timeline, outcome };
};

/**
 * What a node inside the loop did: one entry per iteration with that node's
 * output, read from the carrier's `iterations[].region_results`.
 */
export interface RegionNodeRun {
  iterations: Array<{
    iteration: number;
    output: JsonValue | undefined;
    /** Phase 8d: set when a Switch / If inside the loop skipped this node on this attempt. */
    skipSentence?: string | null;
  }>;
  /** Attempts this node actually ran (skipped attempts not counted). */
  count: number;
  skipped: number;
  last: JsonValue | undefined;
}

export const regionNodeRun = (
  carrierResult: unknown,
  nodeId: string,
): RegionNodeRun | null => {
  const result = parseRepeatControlResult(carrierResult);
  if (!result) return null;
  const iterations = [...result.iterations]
    .sort((a, b) => a.iteration - b.iteration)
    .map((record) => {
      const raw = record.region_results?.[nodeId];
      const skip = isRecord(raw) ? getSkipExplanation(raw) : null;
      return {
        iteration: record.iteration,
        output: skip ? undefined : regionOutputs(record)[nodeId],
        skipSentence: skip ? skippedSentence(skip) : null,
      };
    })
    .filter((entry) => entry.output !== undefined || entry.skipSentence);
  if (iterations.length === 0) return null;
  const ran = iterations.filter((entry) => !entry.skipSentence);
  return {
    iterations,
    count: ran.length,
    skipped: iterations.length - ran.length,
    last: ran.length ? ran[ran.length - 1].output : undefined,
  };
};
